import { useState, useRef, useCallback, useEffect } from "react";

import {
  getInitialPromptHeight,
  savePromptHeight,
  constrainPromptHeight,
  MAX_PROMPT_HEIGHT_RATIO,
} from "@/utils/promptHeight";

interface UseResizablePromptAreaReturn {
  /** Current prompt area height in pixels, or null for auto sizing */
  promptHeight: number | null;
  /** Whether the user is currently resizing the prompt area */
  isResizingPrompt: boolean;
  /** Ref to attach to the prompt area wrapper element */
  promptWrapperRef: React.RefObject<HTMLDivElement | null>;
  /** Handler for starting the resize operation */
  handlePromptResizeMouseDown: (e: React.MouseEvent) => void;
  /** Handler for keyboard-driven resize (delta in pixels) */
  handlePromptResizeKeyboard: (delta: number) => void;
  /** Reset to auto sizing (removes localStorage value) */
  resetPromptHeight: () => void;
  /** Maximum allowed prompt height in pixels (reactive, updates on window resize) */
  maxPromptHeight: number;
}

/**
 * Hook for managing a vertically resizable prompt input area with localStorage persistence.
 * Handles mouse events for dragging, constrains height within bounds,
 * and saves the height to localStorage when resizing completes.
 *
 * Height starts as null (auto/natural sizing). On first drag, the current
 * rendered height is captured and the area switches to controlled mode.
 */
export function useResizablePromptArea(): UseResizablePromptAreaReturn {
  const [promptHeight, setPromptHeight] = useState<number | null>(getInitialPromptHeight);
  const [isResizingPrompt, setIsResizingPrompt] = useState(false);
  const [maxPromptHeight, setMaxPromptHeight] = useState(
    () => window.innerHeight * MAX_PROMPT_HEIGHT_RATIO,
  );
  const promptWrapperRef = useRef<HTMLDivElement>(null);
  const startYRef = useRef<number>(0);
  const startHeightRef = useRef<number>(0);
  const containerHeightRef = useRef<number>(0);
  const animationFrameRef = useRef<number | null>(null);
  const promptHeightRef = useRef(promptHeight);

  promptHeightRef.current = promptHeight;

  const getContainerHeight = useCallback((): number => {
    const parent = promptWrapperRef.current?.parentElement;

    return parent ? parent.getBoundingClientRect().height : window.innerHeight;
  }, []);

  const handlePromptResizeMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();

      const wrapper = promptWrapperRef.current;

      if (!wrapper) return;

      // Capture starting position and current rendered height
      startYRef.current = e.clientY;
      startHeightRef.current = wrapper.getBoundingClientRect().height;
      containerHeightRef.current = getContainerHeight();

      // If height was auto (null), switch to controlled mode with the current rendered height
      if (promptHeight == null) {
        setPromptHeight(startHeightRef.current);
      }

      // Prevent text selection and override cursor during drag
      document.body.style.cursor = "row-resize";
      document.body.style.userSelect = "none";

      setIsResizingPrompt(true);
    },
    [promptHeight, getContainerHeight],
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isResizingPrompt) return;

      // Cancel any pending animation frame
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }

      // Throttle using requestAnimationFrame
      animationFrameRef.current = requestAnimationFrame(() => {
        // Dragging UP (negative clientY delta) = larger prompt area
        const delta = startYRef.current - e.clientY;
        const newHeight = startHeightRef.current + delta;
        const constrained = constrainPromptHeight(newHeight, containerHeightRef.current);

        setPromptHeight((prev) => {
          const diff = Math.abs((prev ?? 0) - constrained);

          return diff > 1 ? constrained : prev;
        });
      });
    },
    [isResizingPrompt],
  );

  const handleMouseUp = useCallback(() => {
    // Clean up animation frame
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    // Restore body cursor and text selection
    document.body.style.cursor = "";
    document.body.style.userSelect = "";

    setIsResizingPrompt(false);

    // Save the current height to localStorage
    setPromptHeight((current) => {
      savePromptHeight(current);

      return current;
    });
  }, []);

  const handlePromptResizeKeyboard = useCallback(
    (delta: number) => {
      const container = getContainerHeight();

      setPromptHeight((prev) => {
        // If currently auto, read the rendered height first
        const current = prev ?? promptWrapperRef.current?.getBoundingClientRect().height ?? 180;
        const newHeight = constrainPromptHeight(current + delta, container);

        savePromptHeight(newHeight);

        return newHeight;
      });
    },
    [getContainerHeight],
  );

  const resetPromptHeight = useCallback(() => {
    setPromptHeight(null);
    savePromptHeight(null);
  }, []);

  // Add/remove global mouse event listeners during resize
  useEffect(() => {
    if (isResizingPrompt) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);

      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isResizingPrompt, handleMouseMove, handleMouseUp]);

  // Re-constrain height on window resize — uses a ref to avoid
  // tearing down and re-adding the listener on every height change during drag.
  // Also keeps maxPromptHeight in sync so ARIA valuemax is always correct.
  useEffect(() => {
    const updateFromContainer = () => {
      const container = getContainerHeight();
      const maxH = container * MAX_PROMPT_HEIGHT_RATIO;

      setMaxPromptHeight(maxH);

      const current = promptHeightRef.current;

      if (current != null && current > maxH) {
        const constrained = constrainPromptHeight(current, container);

        setPromptHeight(constrained);
        savePromptHeight(constrained);
      }
    };

    // Set correct value after mount when the ref is populated
    updateFromContainer();

    const handleWindowResize = () => {
      updateFromContainer();
    };

    window.addEventListener("resize", handleWindowResize);

    return () => window.removeEventListener("resize", handleWindowResize);
  }, [getContainerHeight]);

  return {
    promptHeight,
    isResizingPrompt,
    promptWrapperRef,
    handlePromptResizeMouseDown,
    handlePromptResizeKeyboard,
    resetPromptHeight,
    maxPromptHeight,
  };
}
